import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdClose } from 'react-icons/md';
import PerfectScrollBar from 'react-perfect-scrollbar';
import { useDispatch, useSelector } from 'react-redux';

import { CircularProgress } from '@material-ui/core';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { IState } from '~/store';
import {
  getProfilesRequest,
  getUserProfilesRequest,
  addUserProfileRequest,
  removeUserProfileRequest,
} from '~/store/modules/users/actions';
import { IProfile, IUser } from '~/store/modules/users/types';

import { Container, CustomSelect } from './styles';

interface Props {
  user: IUser;
}

interface FormData {
  profile_id: number;
}

const SecurityProfile: React.FC<Props> = ({ user }) => {
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [availableProfiles, setAvailableProfiles] = useState<IProfile[] | null>(
    null,
  );

  const loadingGroupsOrProfile = useSelector(
    (state: IState) => state.users.loadingGroupsOrProfile,
  );
  const users = useSelector((state: IState) => state.users.users);
  const profiles = useSelector((state: IState) => state.users.profiles);

  const formRef = useRef<FormHandles>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getProfilesRequest());
    dispatch(getUserProfilesRequest(user.id));
  }, [dispatch, user.id]);

  useEffect(() => {
    const userIndex = users.findIndex((findUser) => findUser.id === user.id);

    setSelectedUser(users[userIndex]);
  }, [user.id, users]);

  useEffect(() => {
    if (profiles && selectedUser?.profiles) {
      const filteredProfiles = profiles.filter(
        (profile) =>
          profile.id !==
          selectedUser.profiles.find(
            (findProfile) => findProfile.id === profile.id,
          )?.id,
      );

      setAvailableProfiles(filteredProfiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, selectedUser?.profiles]);

  const handleRemoveProfile = useCallback(
    (user_id, profile) => {
      dispatch(removeUserProfileRequest(user_id, profile));
    },
    [dispatch],
  );

  const handleSubmit: SubmitHandler<FormData> = async (data, { reset }) => {
    try {
      const schema = Yup.object().shape({
        profile_id: Yup.string().required('Escolha uma perfil.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const findedProfile = profiles.find(
        (profile) => profile.id === data.profile_id,
      );

      if (findedProfile === undefined) {
        formRef.current?.setFieldError('profile_id', 'Perfil inválido');
        return;
      }

      dispatch(addUserProfileRequest(user.id, findedProfile));
      reset();
    } catch (err) {
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          Object.assign(validationErrors, {
            [error.path]: error.message,
          });
        });
        formRef.current?.setErrors(validationErrors);
      }
    }
  };

  return (
    <Container>
      {availableProfiles && (
        <>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <CustomSelect
              label="Perfis"
              placeholder="Selecione uma perfil"
              name="profile_id"
              options={availableProfiles.map((profile) => ({
                value: profile.id,
                label: profile.name,
              }))}
              classNamePrefix="react-select"
            />
            <button type="submit" disabled={loadingGroupsOrProfile}>
              {loadingGroupsOrProfile ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Adicionar'
              )}
            </button>
          </Form>
          <PerfectScrollBar
            style={{
              maxHeight: '200px',
              paddingRight: '15px',
              marginTop: '20px',
            }}
          >
            <table>
              <thead>
                <tr>
                  <th>Perfil</th>
                  <th style={{ textAlign: 'right' }}>Remover</th>
                </tr>
              </thead>
              <tbody>
                {selectedUser &&
                  selectedUser.profiles &&
                  selectedUser.profiles.length === 0 && (
                    <tr>
                      <td colSpan={2}>
                        <p>Nenhum perfil atribuído</p>
                      </td>
                    </tr>
                  )}
                {selectedUser &&
                  selectedUser.profiles &&
                  selectedUser?.profiles.map((profile) => (
                    <tr key={`${profile.id}-${profile.name}`}>
                      <td>{profile.name}</td>
                      <td style={{ textAlign: 'right' }}>
                        {selectedUser.profiles.length > 1 && (
                          <button
                            type="button"
                            onClick={
                              () =>
                                handleRemoveProfile(selectedUser.id, profile)
                              // eslint-disable-next-line react/jsx-curly-newline
                            }
                          >
                            {loadingGroupsOrProfile ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <MdClose size={20} />
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </PerfectScrollBar>
        </>
      )}
    </Container>
  );
};

export default SecurityProfile;
